import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { Project, CreateProjectRequest, UpdateProjectRequest } from '@ponzinomics/types';
import { apiClient } from '../lib';
import { useProjectStore } from '../stores';

// Mock mode: use mock data when backend unavailable
// Set VITE_MOCK_API=true in .env to enable, or it auto-enables if no VITE_API_URL
const MOCK_MODE =
  import.meta.env.VITE_MOCK_API === 'true' ||
  (import.meta.env.DEV && !import.meta.env.VITE_API_URL);

const defaultSettings: Project['settings'] = {
  memeGenerator: { enabled: true, dailyLimit: 100, allowedStyles: ['funny'], customPrompts: [] },
  quests: { enabled: true, maxActiveQuests: 5, pointsMultiplier: 1 },
  leaderboard: { enabled: true, resetPeriod: 'never', showTopCount: 100 },
};

const MOCK_PROJECTS: Project[] = [
  {
    id: 'proj-1',
    name: 'Moon Coin',
    slug: 'moon-coin',
    description: 'A meme coin that will take you to the moon',
    ownerId: 'dev-user-1',
    plan: 'free',
    settings: defaultSettings,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'proj-2',
    name: 'Doge Army',
    slug: 'doge-army',
    description: 'Much wow, very coin',
    ownerId: 'dev-user-1',
    plan: 'starter',
    settings: defaultSettings,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
];

// Query keys factory
export const projectKeys = {
  all: ['projects'] as const,
  lists: () => [...projectKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...projectKeys.lists(), filters] as const,
  details: () => [...projectKeys.all, 'detail'] as const,
  detail: (id: string) => [...projectKeys.details(), id] as const,
};

// Fetch all projects
export function useProjects() {
  const { setProjects } = useProjectStore();

  return useQuery({
    queryKey: projectKeys.lists(),
    queryFn: async () => {
      if (MOCK_MODE) {
        setProjects(MOCK_PROJECTS);
        return MOCK_PROJECTS;
      }
      const projects = await apiClient.get<Project[]>('/projects');
      setProjects(projects);
      return projects;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// Fetch single project
export function useProject(id: string | undefined) {
  const { setCurrentProject } = useProjectStore();

  return useQuery({
    queryKey: projectKeys.detail(id!),
    queryFn: async () => {
      if (MOCK_MODE) {
        const project = MOCK_PROJECTS.find((p) => p.id === id) ?? MOCK_PROJECTS[0]!;
        setCurrentProject(project);
        return project;
      }
      const project = await apiClient.get<Project>(`/projects/${id}`);
      setCurrentProject(project);
      return project;
    },
    enabled: Boolean(id),
  });
}

// Create project
export function useCreateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateProjectRequest) => {
      if (MOCK_MODE) {
        const newProject: Project = {
          id: `proj-${Date.now()}`,
          name: data.name,
          slug: data.name.toLowerCase().replace(/\s+/g, '-'),
          ownerId: 'dev-user-1',
          plan: 'free',
          settings: defaultSettings,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        if (data.description) {
          newProject.description = data.description;
        }
        return newProject;
      }
      return apiClient.post<Project>('/projects', data);
    },
    onSuccess: (newProject) => {
      queryClient.setQueryData<Project[]>(projectKeys.lists(), (old) => {
        return old ? [...old, newProject] : [newProject];
      });
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
    },
  });
}

// Update project
export function useUpdateProject() {
  const queryClient = useQueryClient();
  const { updateProject } = useProjectStore();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateProjectRequest }) => {
      if (MOCK_MODE) {
        const base = MOCK_PROJECTS.find((p) => p.id === id) ?? MOCK_PROJECTS[0]!;
        const updatedProject: Project = {
          id: base.id,
          name: data.name ?? base.name,
          slug: base.slug,
          ownerId: base.ownerId,
          plan: base.plan,
          settings: base.settings,
          createdAt: base.createdAt,
          updatedAt: new Date().toISOString(),
        };
        if (data.description !== undefined) {
          updatedProject.description = data.description;
        } else if (base.description) {
          updatedProject.description = base.description;
        }
        return updatedProject;
      }
      return apiClient.patch<Project>(`/projects/${id}`, data);
    },
    onSuccess: (updatedProject) => {
      updateProject(updatedProject.id, updatedProject);
      queryClient.setQueryData<Project>(projectKeys.detail(updatedProject.id), updatedProject);
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
    },
  });
}

// Delete project
export function useDeleteProject() {
  const queryClient = useQueryClient();
  const { removeProject } = useProjectStore();

  return useMutation({
    mutationFn: async (id: string) => {
      if (MOCK_MODE) {
        return id;
      }
      await apiClient.delete(`/projects/${id}`);
      return id;
    },
    onSuccess: (deletedId) => {
      removeProject(deletedId);
      queryClient.removeQueries({ queryKey: projectKeys.detail(deletedId) });
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
    },
  });
}
